import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button, Col, Dropdown, Form, Input, Row, Select, Tooltip } from 'antd';
import apiCalls from 'src/api/apiCalls';
import ErrorMessage from 'src/components/shared/messages/errorMessage';
import { NumericFormat } from 'react-number-format';
import List from 'src/components/grid.component';
import { AddressHandler, DepositHandler, TotalBalance } from './service';
import GridLayout from 'src/components/shared/gridLayout';
import HeaderComponent from 'src/components/header';
import { handleOnKeyDown } from 'src/utils/service';
import WalletDeposit from './deposit';

const { Option } = Select;

const Portfolio = () => {
  const gridRef = useRef(null);
  const [currencies, setCurrencies] = useState([]);
  const [errorMessage, setErrorMessage] = useState(null);
  const [filterValues, setFilterValues] = useState({ programName: "All", currency: "All" });
  const [balances, setBalances] = useState();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();
  const [isRefresh, setIsRefresh] = useState(false);
  const [copied, setCopied] = useState('');
  const [walletData, setWalletData] = useState(null);
  const [depositModelOpen, setDepositModelOpen] = useState(false);

  useEffect(() => {
    getCurrencies();
    getBalances();
  }, []);

  const openModel = useCallback((obj) => () => {
    setDepositModelOpen(true);
    setWalletData(obj);
  }, []);

  const closeModel = useCallback(() => {
    setDepositModelOpen(false);
    setWalletData(null);
  }, []);

  const handleCopy = useCallback((id) => () => {
    setCopied(id);
    setTimeout(() => {
      setCopied('');
    }, 1000);
  }, []);

  const getBalances = async () => {
    const res = await apiCalls.gettotalAssets();
    if (res.ok) {
      setBalances(res.data);
    } else {
      setErrorMessage(apiCalls.isErrorDispaly(res));
    }
  };

  const getCurrencies = async () => {
    const res = await apiCalls.getCardsLookups('WalletCurrencyLu');
    if (res.ok) {
      setCurrencies(res.data?.WalletCurrency);
    } else {
      setErrorMessage(apiCalls.isErrorDispaly(res));
    }
  };

  const handleOpenChange = useCallback((nextOpen, info) => {
    if (info.source === 'trigger' || nextOpen) {
      setOpen(nextOpen);
    }
  }, []);

  const handleMenuClick = (e) => {
    if (e.key === '3') {
      setOpen(false);
    }
  };

  const totalBalance = useCallback((props) => {
    return <TotalBalance data={props?.dataItem} />;
  }, []);

  const addressHandler = useCallback((props) => {
    return <AddressHandler data={props?.dataItem} handleCopy={handleCopy} copied={copied} />;
  }, [copied, handleCopy]);

  const depositHandler = useCallback((props) => {
    return <DepositHandler data={props?.dataItem} openModel={openModel} />;
  }, [openModel]);

  const customcells = {
    balance: totalBalance,
    walletAddress: addressHandler,
    "": depositHandler
  };

  const renderCustomCell = useCallback((props, column) => {
    if (customcells[column.field]) {
      return customcells[column.field](props);
    }
  }, [customcells]);

  const clearRecords = useCallback(() => {
    form.resetFields();
    const cleared = { programName: "All", currency: "All" };
    setFilterValues(cleared);
    setOpen(false);
  }, [form]);

  const searchRecords = useCallback(() => {
    const formValues = form.getFieldsValue();
    const updatedFilter = {
      programName: formValues.programName || "All",
      currency: formValues.currency || "All"
    };
    setFilterValues(updatedFilter);
    setOpen(false);
  }, [form,filterValues]);

  const handleChange = useCallback((e) => {
    form.setFieldsValue({ currency: e });
  }, [form]);

  const inputHandleChange = useCallback((e) => {
    form.setFieldsValue({ programName: e.target?.value });
  }, [form]);

  const refreshCallBack = useCallback(() => {
    setIsRefresh(false);
  }, []);

  const items = [
    {
      label: (
        <Form
          className="grid-filter-form"
          form={form}
          initialValues={{ programName: "", currency: "All" }}
          onFinish={searchRecords}
        >
          <p className="filter-option-title">Filter Options</p>
          <Row gutter={[16]} className="filters-row">
            <Col span={24}>
              <Form.Item
                className="input-style"
                label="Program Name"
                name="programName"
                rules={[
                  {
                    validator(_, value) {
                      if (value && !/^[a-zA-Z0-9\s\-()]+$/.test(value)) {
                        return Promise.reject("Invalid Program name");
                      }
                      return Promise.resolve();
                    }
                  }
                ]}
              >
                <Input
                  placeholder="Enter Program Name"
                  tabIndex={1}
                  onKeyDown={handleOnKeyDown}
                  onChange={inputHandleChange}
                />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="currency" label="Currency">
                <Select
                  placeholder="Select currency"
                  allowClear
                  defaultValue="All"
                  onChange={handleChange}
                >
                  {currencies?.map((item) => (
                    <Option key={item.name} value={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <div className="mt-2 md:flex justify-end gap-2 space-y-2 md:space-y-0">
            <Button className="cancel-btn" onClick={clearRecords} key="back">
              Clear
            </Button>
            <Button className="primary-btn" htmlType="submit">
              Search
            </Button>
          </div>
        </Form>
      ),
      key: '1'
    }
  ];

  const breadCrumbList = [{ bname: 'Wallets' }];

  return (
    <div className="">
      <HeaderComponent breadCrumbList={breadCrumbList} />
      <div className="md:flex justify-between items-end md:!space-y-0 !space-y-3">
        {balances?.totalAssetsInUsd != null ? (
          <div className="border !border-StrokeColor px-3 py-3 md:min-w-80 rounded-md">
            <p className="text-sm font-normal text-paraColor">Total Assets</p>
            <p className="text-md text-subTextColor font-semibold">
              <NumericFormat
                value={balances?.totalAssetsInUsd}
                decimalScale={2}
                thousandSeparator
                thousandsGroupStyle="lakh"
                displayType="text"
              />{' '}
              USDT
            </p>
          </div>
        ) : (
          <p></p>
        )}
        <div className="flex gap-2 justify-end items-center">
          <Dropdown
            overlayClassName="filterdropdown-overlay"
            className="filter-dropdown mt-0"
            menu={{ items, onClick: handleMenuClick }}
            onOpenChange={handleOpenChange}
            open={open}
            trigger={['click']}
          >
            <a onClick={(e) => e.preventDefault()} className="">
              <Tooltip title="Filter">
                <span className="icon filter-icon-dropdown"></span>
              </Tooltip>
            </a>
          </Dropdown>
          <GridLayout />
        </div>
      </div>
      <div className="mb-3">
        {errorMessage && <ErrorMessage msg={errorMessage} />}
      </div>
      <div className="mt-3 table-auto wallets-grid">
        <List
          pSize={10}
          className=""
          pKey="Wallets"
          screenName="Wallets"
          renderCustomCell={renderCustomCell}
          url={`${process.env.REACT_APP_API_END_POINT}/api/CardsActivityService/WalletsGridK`}
          additionalParams={filterValues}
          ref={gridRef}
          refreshCallBack={refreshCallBack}
          refresh={isRefresh}
        />
      </div>
      <WalletDeposit open={depositModelOpen} closeModel={closeModel} WalletData={walletData} />
    </div>
  );
};

export default Portfolio;
